import { RxCross2 } from 'react-icons/rx';
import { FiDownload } from "react-icons/fi";
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import saveAs from 'file-saver';
import useAttachments from '../hook/useAttachments';
import { ticketBaseURL } from '../config/api';
import { useEffect, useState } from 'react';

const AttachmentView = () => {
    const {
        viewImages,
        setViewImages,
        attachments,
        currentIndex,
        setCurrentIndex
    }: any = useAttachments();

    const [csvContent, setCsvContent] = useState<string>("");
    const [isCsvLoading, setIsCsvLoading] = useState<boolean>(false);

    const currentAttachment = attachments?.[currentIndex];
    const fileName = currentAttachment?.fileName || "";

    const fileExtension = fileName
        ?.split(".")
        .pop()
        ?.toLowerCase() || "";

    const fileUrl = currentAttachment?.fileName
        ? `${ticketBaseURL}commonFile/Get?name=${encodeURIComponent(fileName)}`
        : "";

    const isImage = ["jpg", "jpeg", "png", "heif", "heic"].includes(fileExtension);
    const isPdf = fileExtension === "pdf";
    const isCsv = fileExtension === "csv";

    useEffect(() => {
        if (!isCsv || !fileUrl || !viewImages) {
            setCsvContent("");
            return;
        }

        const loadCsv = async () => {
            try {
                setIsCsvLoading(true);

                const response = await fetch(fileUrl);

                if (!response.ok) {
                    throw new Error("Failed to load CSV file");
                }

                const text = await response.text();

                setCsvContent(text);
            } catch (error) {
                console.error("Error loading CSV:", error);
                setCsvContent("");
            } finally {
                setIsCsvLoading(false);
            }
        };

        loadCsv();
    }, [fileUrl, isCsv, viewImages]);

    const downloadFile = async (index: number) => {
        try {
            const attachment = attachments[index];

            if (!attachment?.fileName) return;

            const url =
                `${ticketBaseURL}commonFile/download?name=${encodeURIComponent(
                    attachment.fileName
                )}`;

            const response = await fetch(url);

            if (!response.ok) {
                throw new Error("Failed to download file");
            }

            const blob = await response.blob();

            saveAs(blob, attachment.fileName);
        } catch (error) {
            console.error("File download failed:", error);

            // Fallback
            const attachment = attachments[index];

            if (attachment?.fileName) {
                window.open(
                    `${ticketBaseURL}commonFile/download?name=${encodeURIComponent(
                        attachment.fileName
                    )}`,
                    "_blank"
                );
            }
        }
    };

    const prevAttachment = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const nextAttachment = () => {
        if (currentIndex < attachments.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const renderAttachment = () => {
        // Image
        if (isImage) {
            return (
                <img
                    src={fileUrl}
                    alt={fileName}
                    className="max-w-full max-h-full rounded-lg object-contain"
                />
            );
        }

        // Pdf
        if (isPdf) {
            return (
                <iframe
                    src={fileUrl}
                    title={fileName}
                    className="w-full h-full rounded-lg bg-white"
                />
            );
        }

        // Csv    
        if (isCsv) {
            return (
                <div className="w-full h-full rounded-lg bg-white overflow-auto p-6">
                    {isCsvLoading ? (
                        <div className="w-full h-full flex items-center justify-center">
                            <div className="text-gray-600 font-semibold">
                                Loading CSV...
                            </div>
                        </div>
                    ) : csvContent ? (
                        <pre className="text-sm text-black whitespace-pre-wrap font-mono">
                            {csvContent}
                        </pre>
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <div className="text-gray-500">
                                Unable to display CSV file.
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        // Unknow File
        return (
            <div className="w-full h-full flex flex-col justify-center items-center rounded-lg bg-white">
                <div className="text-6xl mb-4">
                    📎
                </div>

                <div className="text-lg font-semibold text-gray-700">
                    {fileName}
                </div>

                <div className="text-sm text-gray-500 mt-2">
                    Preview is not available for this file type.
                </div>

                <button
                    type="button"
                    onClick={() => downloadFile(currentIndex)}
                    className="mt-5 px-5 py-2 bg-secondary text-white rounded font-semibold"
                >
                    Download File
                </button>
            </div>
        );
    }

    return (
        <div
            className={`
                ${viewImages ? "block" : "hidden"}
                fixed inset-0 z-[10000]
                before:fixed before:inset-0
                before:w-full before:h-full
                before:bg-black/80
            `}
        >
            <div className="w-full h-full flex flex-col relative z-[10001]">
                {/* Header */}
                <div className="flex justify-between items-center py-2.5 px-6 w-full h-16">

                    {/* File Name */}
                    <div className="w-1/3 flex justify-start">
                        <span
                            className="font-semibold text-base text-white truncate max-w-full"
                            title={fileName}
                        >
                            {fileName || "-"}
                        </span>
                    </div>

                    {/* Download */}
                    <div className="w-1/3 flex justify-center">
                        <button
                            type="button"
                            onClick={() => downloadFile(currentIndex)}
                            className="
                                px-6 py-2
                                flex justify-center items-center gap-2
                                text-white text-sm font-semibold
                                outline-none bg-transparent
                                hover:opacity-80
                            "
                            title="Download"
                        >
                            <FiDownload size={24} />
                        </button>
                    </div>

                    {/* Close */}
                    <div className="w-1/3 flex justify-end">
                        <button
                            type="button"
                            onClick={() => setViewImages(false)}
                            className="
                                px-6 py-2
                                flex justify-center items-center gap-2
                                text-white text-sm font-semibold
                                outline-none bg-transparent
                                hover:opacity-80
                            "
                            title="Close"
                        >
                            <RxCross2 size={24} />
                        </button>
                    </div>

                </div>

                {/* Context */}
                <div className="flex justify-between items-center py-2.5 px-6 w-full flex-1 min-h-0">

                    {/* Previous */}
                    <div className="w-1/12 flex justify-start">
                        <button
                            type="button"
                            onClick={prevAttachment}
                            disabled={currentIndex === 0}
                            className={`
                                w-8 h-8
                                flex justify-center items-center
                                p-0.5 rounded-full
                                text-white text-sm font-semibold
                                border border-white
                                outline-none bg-transparent
                                ${currentIndex === 0
                                    ? "opacity-30 cursor-default"
                                    : "opacity-100 hover:opacity-80 cursor-pointer"
                                }
                            `}
                        >
                            <FaChevronLeft />
                        </button>
                    </div>

                    {/* File Preview */}
                    <div className="flex w-10/12 h-full justify-center items-center min-h-0">
                        {renderAttachment()}
                    </div>

                    {/* Next */}
                    <div className="w-1/12 flex justify-end">
                        <button
                            type="button"
                            onClick={nextAttachment}
                            disabled={currentIndex >= attachments.length - 1}
                            className={`
                                w-8 h-8
                                flex justify-center items-center
                                p-0.5 rounded-full
                                text-white text-sm font-semibold
                                border border-white
                                outline-none bg-transparent
                                ${currentIndex >= attachments.length - 1
                                    ? "opacity-30 cursor-default"
                                    : "opacity-100 hover:opacity-80 cursor-pointer"
                                }
                            `}
                        >
                            <FaChevronRight />
                        </button>
                    </div>

                </div>

            </div>
        </div>
    );
};

export default AttachmentView;
